import { logger } from "@/shared/lib/logger";
import type { AxiosInstance } from "axios";
import type { ApiAlertResponse, ApiAlertTuple } from "../types/alert.types";
import { apiRoutes } from "@/shared/api/routes";

/**
 * React Query key for getEnabledAlertTypes (header alert badge + Alert details). Its own root
 * name: the Alerts Type dropdown caches search pages under ["alertTypes", searchTerm], so an
 * ["alertTypes", "enabled"] key would collide when someone searches "enabled".
 */
export const ENABLED_ALERT_TYPES_QUERY_KEY = ["enabledAlertTypes"] as const;

export const getAlertsApi = (apiClient: AxiosInstance) => ({
  getAlertsList: async (
    page: number,
    limit: number,
    query: unknown[] = [],
    orderParam?: [string, string][]
  ): Promise<ApiAlertResponse> => {
    const requestBody = {
      select: [
        "checklist_ref",
        "comment",
        "driver_first_name",
        "driver_last_name",
        "id",
        "info",
        "notify_driver",
        "origin_agent",
        "resolve_agent",
        "resolve_timestamp",
        "run_public_id",
        "stop_public_id",
        "timestamp",
        "type",
        "vehicle_public_id",
        "customer.id",
        "driver.id",
        "metaroute.id",
        "resolve_type.id",
        "run_company.id",
        "trip.id",
        "trip.noshow_reason",
      ],
      query: [...query],
      options: {
        timeout: 10,
        offset: page * limit,
        limit: limit,
        order: orderParam || [
          ["timestamp", "desc_nulls_last"],
          ["id", "asc"],
        ],
        calculate_total: true,
        return_results: true,
        select_distinct: true,
      },
    };

    try {
      const response = await apiClient.post<ApiAlertResponse>(apiRoutes.proxy("support/internal/alert/select_query"),
        requestBody
      );
      return response.data;
    } catch (error) {
      logger.error("Failed to fetch alerts list:", error);
      throw new Error("Failed to load alerts list");
    }
  },
  getAlertById: async (id: string): Promise<ApiAlertTuple | null> => {
    const requestBody = {
      select: [
        "checklist_ref",
        "comment",
        "driver_first_name",
        "driver_last_name",
        "id",
        "info",
        "notify_driver",
        "origin_agent",
        "resolve_agent",
        "resolve_timestamp",
        "run_public_id",
        "stop_public_id",
        "timestamp",
        "type",
        "vehicle_public_id",
        "customer.id",
        "driver.id",
        "metaroute.id",
        "resolve_type.id",
        "run_company.id",
        "trip.id",
        "trip.noshow_reason",
      ],
      query: [["equal", "id", parseInt(id, 10)]],
      options: {
        timeout: 10,
        offset: 0,
        limit: 1,
        calculate_total: false,
        return_results: true,
        select_distinct: true,
      },
    };

    try {
      const response = await apiClient.post<ApiAlertResponse>(apiRoutes.proxy("support/internal/alert/select_query"),
        requestBody
      );
      const alertData =
        response.data.data && response.data.data.length > 0 ? response.data.data[0] : null;
      return alertData;
    } catch (error) {
      logger.error(`Failed to fetch alert with id ${id}:`, error);
      throw new Error(`Failed to load alert with id ${id}`);
    }
  },
  /**
   * Fetch all enabled alert types with their group mapping.
   * Matches RoR AlertType.enabled_alert_type_ids / AlertType.group_types (DB-driven).
   */
  /**
   * Aggregate type counts for the current alerts query — used by the summary
   * badges (Total / Emergency / Trip / Schedule / Driver / Inspection /
   * Outsourcing / System). Selects only `type` for the matching set so we can
   * count unbounded by the page-size limit.
   */
  getAlertTypeCountsForQuery: async (
    query: unknown[] = []
  ): Promise<{ countsByType: Map<number, number>; total: number }> => {
    const requestBody = {
      select: ["id", "type"],
      query,
      options: {
        timeout: 15,
        offset: 0,
        limit: 10000,
        calculate_total: true,
        return_results: true,
        select_distinct: false,
      },
    };
    try {
      const response = await apiClient.post<{ data?: unknown[][]; info?: { total_results?: number } }>(
        apiRoutes.proxy("support/internal/alert/select_query"),
        requestBody
      );
      const countsByType = new Map<number, number>();
      (response.data?.data ?? []).forEach((row) => {
        const t = Number(row[1]);
        if (!Number.isFinite(t)) return;
        countsByType.set(t, (countsByType.get(t) ?? 0) + 1);
      });
      const total = response.data?.info?.total_results ?? Array.from(countsByType.values()).reduce((a, b) => a + b, 0);
      return { countsByType, total };
    } catch (error) {
      logger.error("Failed to fetch alert type counts:", error);
      return { countsByType: new Map(), total: 0 };
    }
  },

  /**
   * ALERT TYPE SCOPE — one rule, several copies: KEEP THEM IN SYNC.
   * Counted alert types = the agency's ENABLED types from K3 `alert_type`:
   *   enabled = true OR type = 13 (Vehicle Emergency), excluding message types 11 / 12;
   *   no enabled type → no type filter.
   * Source of truth: legacy drt_ui AlertType.enabled_alert_type_ids (app/models/alert_type.rb),
   * used by its Alerts page (AlertQuery#scope_default) and header badge
   * (AlertQuery#statistic_expr_status_alert_count).
   * Copies — change one, change all:
   *   - dispatcher_ui  src/features/operations/api/alertsApi.ts         getEnabledAlertTypes / getAlertTypes (Alerts page)
   *   - dispatcher_ui  src/shared/hooks/useNotificationCount.ts         header alert badge
   *   - transitos      ui/apps/dispatcher-mfe/src/features/dashboard/api/alertsSummaryLive.ts
   *                    fetchEnabledAlertTypeIds (dashboard Alert summary widget, Live)
   *   - dispatcher_bff src/ods/ods.service.ts                            enabledAlertTypeSql (Alert summary, Historical — Databricks k3_alert_types)
   *   - dispatcher_bff src/dashboard/dashboard-widgets.definitions.ts    ENABLED_ALERT_TYPES_BODY + dashboard-widgets.service.ts enabledAlertTypeIds
   */
  getEnabledAlertTypes: async (): Promise<{
    enabledTypeIds: number[];
    groupTypesMap: Record<string, number[]>;
    /** Maps alert type integer → DB description string (mirrors AlertType#get_name fallback).
     *  The React app uses ALERT_TYPE_DESCRIPTIONS (from the en locale) as the primary lookup,
     *  falling back to this DB description for DB-driven types (outsource, inspection, etc.)
     *  that have no hardcoded i18n entry — exactly matching the legacy
     *  human_attribute_localize(:type, type, description) chain. */
    typeDescriptions: Map<number, string>;
  }> => {
    // Mirror RoR AlertType.MESSAGE_TYPES: inbound (11) and outbound (12) messages are
    // excluded from the enabled_alert_type_ids scope — they belong to the Messages section,
    // not the Alerts section.
    const MESSAGE_TYPE_INBOUND = 11;
    const MESSAGE_TYPE_OUTBOUND = 12;
    const MESSAGE_TYPES = new Set([MESSAGE_TYPE_INBOUND, MESSAGE_TYPE_OUTBOUND]);

    const requestBody = {
      // Added "description" (index 3) — the K3 alert_type.description field.
      // This is the fallback label used by AlertType#get_name when no i18n
      // key exists for the type (e.g. DB-driven outsource/inspection types).
      select: ["id", "alert_group", "type", "description"],
      // Mirror RoR: enabled_types = enabled || type == ALERT_TYPE_VEHICLE_EMERGENCY (13)
      // Then enabled_alert_type_ids additionally excludes MESSAGE_TYPES (11, 12)
      query: [
        ["and",
          ["or", ["equal", "enabled", true], ["equal", "type", 13]],
          ["not", ["in", "type", [MESSAGE_TYPE_INBOUND, MESSAGE_TYPE_OUTBOUND]]],
        ],
      ],
      options: {
        timeout: 10,
        offset: 0,
        limit: 1000,
        order: [["type", "asc_nulls_last"]],
        calculate_total: false,
        return_results: true,
        select_distinct: true,
      },
    };

    try {
      const response = await apiClient.post<{ data?: unknown[] }>(
        apiRoutes.proxy("support/internal/alert_type/select_query"),
        requestBody
      );
      const rows = Array.isArray(response.data?.data) ? response.data.data : [];
      const enabledTypeIds: number[] = [];
      const groupTypesMap: Record<string, number[]> = {};
      const typeDescriptions = new Map<number, string>();

      // DB stores abbreviated alert_group values (e.g. "emerg") — normalize to canonical UI names.
      const aliases: Record<string, string> = {
        emerg: "emergency",
        sched: "schedule",
        insp: "inspection",
        outsrc: "outsourcing",
        sys: "system",
      };

      for (const row of rows) {
        if (!Array.isArray(row)) continue;
        // select: ["id", "alert_group", "type", "description"]
        // → row[1]=alert_group, row[2]=type, row[3]=description
        const raw = String(row[1] ?? "").trim().toLowerCase();
        const alertGroup = aliases[raw] ?? raw;
        const type = Number(row[2]);
        if (!Number.isFinite(type)) continue;
        // Double-check: never include message types (defensive guard)
        if (MESSAGE_TYPES.has(type)) continue;
        enabledTypeIds.push(type);
        if (alertGroup) {
          (groupTypesMap[alertGroup] ??= []).push(type);
        }
        // Collect DB description for K3-driven fallback (mirrors AlertType#get_name)
        const description = String(row[3] ?? "").trim();
        if (description) {
          typeDescriptions.set(type, description);
        }
      }

      return { enabledTypeIds, groupTypesMap, typeDescriptions };
    } catch (error) {
      logger.error("Failed to fetch enabled alert types:", error);
      throw new Error("Failed to load enabled alert types");
    }
  },

  getAlertTypes: async (offset: number = 0, limit: number = 30, searchTerm: string = '') => {
    // ALERT TYPE SCOPE — same rule as getEnabledAlertTypes above; keep every copy listed there in sync.
    // Mirror legacy AlertType.enabled_alert_type_ids
    // (drt_ui app/models/alert_type.rb): the enabled set is
    //   `(enabled=true OR type=13 ALERT_TYPE_VEHICLE_EMERGENCY)`
    // and then MESSAGE_TYPES (type=11 INBOUND, type=12 OUTBOUND) are
    // explicitly excluded. The previous query missed the MESSAGE_TYPES
    // exclusion, so any enabled inbound/outbound message types showed up
    // in the Alerts Type filter dropdown when legacy hides them. Closes
    // QA #53.
    const enabledFilter: unknown[] = ["or", ["equal", "enabled", true], ["equal", "type", 13]];
    const notMessageTypes: unknown[] = ["not", ["in", "type", [11, 12]]];
    const baseFilter: unknown[] = ["and", enabledFilter, notMessageTypes];
    const requestBody = {
      select: ["id", "alert_group", "type", "description", "enabled"],
      query: [
        searchTerm
          ? ["and", baseFilter, ["like", "description", `%${searchTerm}%`]]
          : baseFilter,
      ],
      options: {
        timeout: 10,
        offset,
        limit,
        order: [["type", "asc_nulls_last"]],
        calculate_total: true,
        return_results: true,
        select_distinct: true,
      },
    };

    try {
      const response = await apiClient.post(apiRoutes.proxy("support/internal/alert_type/select_query"),
        requestBody,
        {
          headers: {
            "Accept-Language": "en-US",
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      logger.error("Failed to fetch alert types:", error);
      throw new Error("Failed to load alert types");
    }
  },

  getRunIdsByPublicIds: async (publicIds: string[]): Promise<Map<string, { runId: number; companyId: number | null; companyName: string | null }>> => {
    if (!publicIds.length) {
      return new Map<string, { runId: number; companyId: number | null; companyName: string | null }>();
    }

    const requestBody = {
      select: ["id", "public_id", "company.id", "company.name"],
      query: [["in", "public_id", publicIds]],
      options: {
        timeout: 10,
        offset: 0,
        limit: 1000,
        calculate_total: false,
        return_results: true,
        select_distinct: true,
      },
    };

    const response = await apiClient.post<{ data?: unknown[] }>(
      apiRoutes.proxy("support/internal/run/select_query"),
      requestBody
    );

    const result = new Map<string, { runId: number; companyId: number | null; companyName: string | null }>();
    const rows = Array.isArray(response.data?.data) ? response.data.data : [];

    rows.forEach((row) => {
      if (!Array.isArray(row) || row.length < 2) return;
      const id = Number(row[0]);
      const publicId = String(row[1] ?? "").trim();
      const companyId = row[2] != null ? Number(row[2]) : null;
      const companyName = row[3] != null ? String(row[3]).trim() : null;
      if (Number.isFinite(id) && publicId) {
        result.set(publicId, {
          runId: id,
          companyId: Number.isFinite(companyId) ? companyId : null,
          companyName: companyName || null,
        });
      }
    });

    return result;
  },

  getVehicleIdsByPublicIds: async (publicIds: string[]): Promise<Map<string, number>> => {
    if (!publicIds.length) {
      return new Map<string, number>();
    }

    const requestBody = {
      select: ["id", "public_id"],
      query: [["in", "public_id", publicIds]],
      options: {
        timeout: 10,
        offset: 0,
        limit: 1000,
        calculate_total: false,
        return_results: true,
        select_distinct: true,
      },
    };

    const response = await apiClient.post<{ data?: unknown[] }>(
      apiRoutes.proxy("support/internal/vehicle/select_query"),
      requestBody
    );

    const result = new Map<string, number>();
    const rows = Array.isArray(response.data?.data) ? response.data.data : [];

    rows.forEach((row) => {
      if (!Array.isArray(row) || row.length < 2) return;
      const id = Number(row[0]);
      const publicId = String(row[1] ?? "").trim();
      if (Number.isFinite(id) && publicId) {
        result.set(publicId, id);
      }
    });

    return result;
  },

  /**
   * Fetches the fields legacy `Alert#can_send_message?` depends on for the
   * alert's vehicle: `enabled` and `delivery_system`. Drives the Send
   * Message button visibility on the alert details page.
   *
   * Mirrors the chain in drt_ui:
   *   Alert#can_send_message?  → mf_vehicle.can_send_message?
   *   Vehicle#can_send_message? → enabled && mf_mdt
   *   Vehicle#mf_mdt           → delivery_system && delivery_system != 9 (MANUAL)
   */
  getVehicleSendMessageStatus: async (vehicleId: number): Promise<{ enabled: boolean; canSendMessage: boolean } | null> => {
    try {
      const response = await apiClient.post<{ data?: unknown[] }>(
        apiRoutes.proxy("support/internal/vehicle/select_query"),
        {
          select: ["id", "enabled", "delivery_system"],
          query: [["equal", "id", vehicleId]],
          options: { timeout: 10, offset: 0, limit: 1, calculate_total: false, return_results: true, select_distinct: true },
        },
      );
      const rows = Array.isArray(response.data?.data) ? response.data.data : [];
      const row = rows[0];
      if (!Array.isArray(row) || row.length < 3) return null;
      const enabled = row[1] === true || row[1] === 1 || row[1] === "true";
      const deliverySystem = row[2];
      // DB::DELIVERY_SYSTEM_MANUAL = 9 — vehicles set to manual delivery have
      // no MDT and therefore cannot receive messages from the dispatcher.
      const hasMdt = deliverySystem != null && Number(deliverySystem) !== 9;
      return { enabled, canSendMessage: enabled && hasMdt };
    } catch {
      return null;
    }
  },
  getCustomerNameById: async (customerId: number): Promise<string | null> => {
    try {
      const response = await apiClient.post<{ data?: unknown[] }>(
        apiRoutes.proxy("support/internal/customer/select_query"),
        {
          select: ["id", "name", "customer_number"],
          query: [["equal", "id", customerId]],
          options: { timeout: 10, offset: 0, limit: 1, calculate_total: false, return_results: true, select_distinct: true },
        }
      );
      const row = Array.isArray(response.data?.data) ? response.data.data[0] : null;
      if (!Array.isArray(row) || row.length < 2) return null;
      const name = String(row[1] ?? "").trim();
      const customerNumber = row[2] != null ? String(row[2]).trim() : "";
      // Format: "Name (customerNumber)" — name as-is from API, number in parens
      return name && customerNumber ? `${name} (${customerNumber})` : name || null;
    } catch {
      return null;
    }
  },

  /**
   * Batch-resolves customer names for multiple IDs at once.
   * Returns a Map<customerId, "Name (customerNumber)"> for all resolved entries.
   */
  getCustomerNamesByIds: async (customerIds: number[]): Promise<Map<number, string>> => {
    if (!customerIds.length) return new Map<number, string>();
    try {
      const response = await apiClient.post<{ data?: unknown[] }>(
        apiRoutes.proxy("support/internal/customer/select_query"),
        {
          select: ["id", "name", "customer_number"],
          query: [["in", "id", customerIds]],
          options: { timeout: 10, offset: 0, limit: customerIds.length + 10, calculate_total: false, return_results: true, select_distinct: true },
        }
      );
      const rows = Array.isArray(response.data?.data) ? response.data.data : [];
      const result = new Map<number, string>();
      rows.forEach((row) => {
        if (!Array.isArray(row) || row.length < 2) return;
        const id = Number(row[0]);
        const name = String(row[1] ?? "").trim();
        const customerNumber = row[2] != null ? String(row[2]).trim() : "";
        if (Number.isFinite(id) && id > 0) {
          const display = name && customerNumber ? `${name} (${customerNumber})` : name || null;
          if (display) result.set(id, display);
        }
      });
      return result;
    } catch {
      return new Map<number, string>();
    }
  },

  getCompanyNameById: async (companyId: number): Promise<string | null> => {
    try {
      const response = await apiClient.post<{ data?: unknown[] }>(
        apiRoutes.proxy("support/internal/company/select_query"),
        {
          select: ["id", "name"],
          query: [["equal", "id", companyId]],
          options: { timeout: 10, offset: 0, limit: 1, calculate_total: false, return_results: true, select_distinct: true },
        }
      );
      const row = Array.isArray(response.data?.data) ? response.data.data[0] : null;
      if (!Array.isArray(row) || row.length < 2) return null;
      return String(row[1] ?? "").trim() || null;
    } catch {
      return null;
    }
  },

  /**
   * Resolves a `resolve_agent` login string (the value stored on
   * `alert.resolve_agent`) to its numeric user id, so the alert details page
   * can render the Resolve agent value as a hyperlink to /system/users/:id.
   * Returns null when the login can't be matched.
   */
  getUserIdByLogin: async (login: string): Promise<number | null> => {
    const trimmed = (login || "").trim();
    if (!trimmed) return null;
    try {
      const response = await apiClient.post<{ data?: unknown[] }>(
        apiRoutes.proxy("support/internal/user/select_query"),
        {
          select: ["id", "login"],
          query: [["equal", "login", trimmed]],
          options: { timeout: 10, offset: 0, limit: 1, calculate_total: false, return_results: true, select_distinct: true },
        },
      );
      const rows = Array.isArray(response.data?.data) ? response.data.data : [];
      const row = rows[0];
      if (!Array.isArray(row) || row.length < 1) return null;
      const userId = Number(row[0]);
      return Number.isFinite(userId) && userId > 0 ? userId : null;
    } catch {
      return null;
    }
  },

  /**
   * Batch-resolves stop sequence numbers (0-based `number` + 1) for
    * multiple stop_public_ids. Returns Map<stop_public_id, sequenceNumber>.
    * Mirrors legacy: mf_stop.number + 1 (alerts_helper.rb line 14).
    * Uses stop/select_query — the live schedule stop resource.
    */
   getStopNumbersByPublicIds: async (publicIds: string[]): Promise<Map<string, number>> => {
     if (!publicIds.length) return new Map<string, number>();
     try {
       const response = await apiClient.post<{ data?: unknown[] }>(
         apiRoutes.proxy("support/internal/stop/select_query"),
         {
           select: ["public_id", "number"],
           query: [["in", "public_id", publicIds]],
           options: {
             timeout: 10,
             offset: 0,
             limit: publicIds.length + 10,
             calculate_total: false,
             return_results: true,
             select_distinct: true,
           },
         },
       );
       const rows = Array.isArray(response.data?.data) ? response.data.data : [];
       const result = new Map<string, number>();
       rows.forEach((row) => {
         if (!Array.isArray(row) || row.length < 2) return;
         const publicId = String(row[0] ?? "").trim();
         const number = Number(row[1]);
         if (publicId && Number.isFinite(number)) {
           result.set(publicId, number + 1); // 0-based → 1-based
         }
       });
       return result;
     } catch {
       return new Map<string, number>();
     }
   },
  getAlertResolveTypes: async (): Promise<{ id: number; description: string }[]> => {
    try {
      const response = await apiClient.post<{ data?: unknown[] }>(
        apiRoutes.proxy("support/internal/alert_resolve_type/select_query"),
        {
          select: ["id", "description"],
          query: [],
          options: {
            timeout: 10,
            offset: 0,
            limit: 100,
            order: [["description", "asc_nulls_last"]],
            calculate_total: false,
            return_results: true,
            select_distinct: true,
          },
        }
      );
      const rows = Array.isArray(response.data?.data) ? response.data.data : [];
      return rows
        .filter((row) => Array.isArray(row) && row.length >= 2)
        .map((row) => ({
          id: Number((row as unknown[])[0]),
          description: String((row as unknown[])[1] ?? "").trim(),
        }))
        .filter((rt) => Number.isFinite(rt.id) && rt.description);
    } catch (error) {
      logger.error("Failed to fetch alert resolve types:", error);
      return [];
    }
  },
});

export type AlertsApi = ReturnType<typeof getAlertsApi>;
